import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Table, Popover, Modal, Input } from 'antd';
import { filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import * as programInvoiceApi from '@apis/program-invoices';
import {
  ProgramInvoice,
  WEEKDAY_LABEL,
  INPUT_OPTION_LABEL,
  CreateProgramInvoiceDto,
  UpdateProgramInvoiceDto,
} from '@models/program-invoice';
import { SearchPagenateQuery } from '@models/common';
import { toast } from 'react-toastify';
import ProgramInvoiceEditView from '@components/modal/program-invoice/ProgramInvoiceEditView';
import { useProgramInvoiceEditStore } from '@stores/programInvoiceEditStore';
import ProgramInvoiceDetailView from '@components/modal/program-invoice/ProgramInvoiceDetailView';

export const ProgramInvoiceModal: React.FC<{
  mode: 'create' | 'edit';
  programInvoice?: ProgramInvoice;
  onClose: () => void;
  onSuccess: () => void;
}> = (props) => {
  const { mode, programInvoice, onClose, onSuccess } = props;
  const { data: editData, setData, resetData } = useProgramInvoiceEditStore();
  const queryClient = useQueryClient();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && programInvoice) {
      setData(() => programInvoice);
    } else {
      resetData();
    }
  }, [mode, programInvoice, setData, resetData]);

  const createMutation = useMutation({
    mutationFn: (dto: CreateProgramInvoiceDto) => programInvoiceApi.createProgramInvoice(dto),
    onSuccess: () => {
      toast.success('수강증이 생성되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['admin-program-invoices'] });
      onSuccess();
    },
    onError: () => toast.error('수강증 생성 중 오류가 발생했습니다.'),
  });

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateProgramInvoiceDto) => programInvoiceApi.updateProgramInvoice(programInvoice!._id, dto),
    onSuccess: () => {
      toast.success('수강증이 수정되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['admin-program-invoices'] });
      onSuccess();
    },
    onError: () => toast.error('수강증 수정 중 오류가 발생했습니다.'),
  });

  const handleReview = () => {
    if (!editData.seller?.id || !editData.representative?.name?.trim() || !editData.product) {
      toast.warn('파트너, 수신자 이름, 상품은 필수 입력 항목입니다.');
      return;
    }
    setIsReviewModalOpen(true);
  };

  const handleSubmit = () => {
    const finalData = { ...editData };
    if (mode === 'create') {
      createMutation.mutate(finalData);
    } else {
      updateMutation.mutate(finalData);
    }
    setIsReviewModalOpen(false);
  };

  const handleClose = () => {
    resetData();
    onClose();
  };

  return (
    <>
      <Modal
        title={mode === 'create' ? '결제창 발송' : '수강증 수정'}
        width={800}
        open={true}
        onCancel={handleClose}
        onOk={handleReview}
        okText="검토"
        cancelText="취소"
        centered>
        <ProgramInvoiceEditView />
      </Modal>

      <Modal
        title="수강증 검토"
        width={600}
        open={isReviewModalOpen}
        onCancel={() => setIsReviewModalOpen(false)}
        onOk={handleSubmit}
        okText={mode === 'create' ? '발송' : '수정'}
        cancelText="취소"
        centered
        confirmLoading={createMutation.isPending || updateMutation.isPending}>
        <ProgramInvoiceDetailView invoice={editData} />
      </Modal>
    </>
  );
};

const ProgramInvoicesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState<SearchPagenateQuery>({
    page: 0,
    size: 20,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedProgramInvoice, setSelectedProgramInvoice] = useState<ProgramInvoice | undefined>();

  const programInvoicesQuery = useQuery({
    queryKey: ['admin-program-invoices', searchQuery],
    queryFn: () => programInvoiceApi.searchProgramInvoices(searchQuery),
    staleTime: 1000 * 60,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => programInvoiceApi.deleteProgramInvoice(id),
    onSuccess: () => {
      toast.success('수강증이 삭제되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['admin-program-invoices'] });
    },
    onError: () => {
      toast.error('수강증 삭제 중 오류가 발생했습니다.');
    },
  });

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: '정말로 이 수강증을 삭제하시겠습니까?',
      content: '삭제된 데이터는 복구할 수 없습니다.',
      okText: '삭제',
      cancelText: '취소',
      onOk: () => {
        deleteMutation.mutate(id);
      },
    });
  };

  const TABLE_COLUMNS: ColumnsType<ProgramInvoice> = [
    {
      title: '파트너명',
      width: '150px',
      render: (record) => record.seller?.name || '-',
      align: 'center',
    },
    {
      title: '수정',
      width: '80px',
      render: (record) => (
        <Button
          onClick={() => {
            setSelectedProgramInvoice({ ...record });
            setModalMode('edit');
            setIsEditModalOpen(true);
          }}>
          수정
        </Button>
      ),
      align: 'center',
    },
    {
      title: '보기',
      width: '80px',
      render: (record) => (
        <Button
          onClick={() => {
            setSelectedProgramInvoice(record);
            setIsViewModalOpen(true);
          }}>
          보기
        </Button>
      ),
      align: 'center',
    },
    {
      title: '삭제',
      width: '80px',
      render: (record) => (
        <Button type="primary" danger onClick={() => handleDelete(record._id)}>
          삭제
        </Button>
      ),
      align: 'center',
    },
    {
      title: '수강증 이름',
      width: '300px',
      render: (record) => (
        <div>
          <div>{record?.title}</div>
          <Popover content={record.product?.name}>
            <div
              style={{
                margin: 'auto',
                width: '300px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {record.product?.name}
            </div>
          </Popover>
        </div>
      ),
      align: 'center',
    },
    {
      title: '구매자',
      render: (record) => {
        const type = record?.representative?.id ? '회원' : '비회원';
        return `${record?.representative?.name}(${type})\n${record?.representative?.phone}`;
      },
      align: 'center',
    },
    {
      title: '수업일정',
      align: 'center',
      render: (record: ProgramInvoice) => {
        const invoiceSchedule = record?.invoiceSchedule;
        if (!invoiceSchedule) return '-';
        let datesText = '-';
        if (invoiceSchedule?.type === 'day') {
          datesText =
            invoiceSchedule?.dates?.map((date) => moment(new Date(date)).format('YYYY.MM.DD HH:mm')).join('\n') || '-';
        } else if (invoiceSchedule?.type === 'week') {
          datesText =
            invoiceSchedule?.weekdays?.map((weekday: string) => `${WEEKDAY_LABEL[weekday]}요일`).join(', ') || '-';
          datesText = `${datesText}\n${invoiceSchedule?.time?.hour}시`;
          if (invoiceSchedule?.time?.minute) {
            datesText = `${datesText} ${invoiceSchedule?.time?.minute}분`;
          }
        } else if (invoiceSchedule?.type === 'month') {
          datesText = `주 ${invoiceSchedule?.weeklyFrequency}회`;
          datesText = `${datesText}\n${invoiceSchedule?.time?.hour}시`;
          if (invoiceSchedule?.time?.minute) {
            datesText = `${datesText} ${invoiceSchedule?.time?.minute}분`;
          }
        }
        return <div>{datesText}</div>;
      },
    },
    {
      title: '수강생',
      align: 'center',
      render: (record: ProgramInvoice) => {
        if (record?.requestParticipantsInfo?.enable) {
          return `입력 요청: ${record?.requestParticipantsInfo?.inputOptions
            ?.map((option) => INPUT_OPTION_LABEL[option])
            .join(', ')}`;
        }
        return (
          record?.participants?.map((participant) => `${participant.name} (${participant.birthYear}년생)`).join('\n') ||
          '-'
        );
      },
    },
    {
      title: '결제요청금액',
      render: (record) => (
        <div>
          <div>{`${record.amount?.toLocaleString() || 0}원`}</div>
          {record.groupDiscountTarget > 0 && <div>{`(${record.groupDiscountTarget}명 그룹할인 적용)`}</div>}
        </div>
      ),
      align: 'center',
    },
    {
      title: '결제유효시간',
      dataIndex: 'validUntil',
      align: 'center',
      render: (validUntil: Date) => (validUntil ? moment(validUntil).format('YYYY.MM.DD HH:mm') : '-'),
    },
    {
      title: '상태',
      dataIndex: 'status',
      align: 'center',
    },
    {
      title: '반복발행',
      align: 'center',
      render: (record: ProgramInvoice) => {
        if (record?.repeat) {
          if (record?.repeatDayOfWeek) {
            return `매주 ${WEEKDAY_LABEL[record?.repeatDayOfWeek]} 전송\n${
              record?.lastSentAt ? moment(record?.lastSentAt).format('YYYY.MM.DD HH:mm') : '-'
            }`;
          }
          return `매월 ${record?.repeatDayOfMonth}일 전송\n${
            record?.lastSentAt ? moment(record?.lastSentAt).format('YYYY.MM.DD HH:mm') : '-'
          }`;
        }
        return '없음';
      },
    },
    {
      title: '메모',
      dataIndex: 'memo',
      align: 'center',
    },
  ];

  return (
    <div className="program-invoice-page dashboard">
      <div className="page-header">
        <div>수강증 관리</div>
      </div>
      <div className="page-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Input
            className={filterActiveClassName(!!searchQuery.keyword)}
            placeholder="파트너명, 구매자, 상품명, 연락처 검색"
            onChange={(e) => {
              const { value } = e.target;
              setSearchQuery((prev) => ({ ...prev, keyword: value, page: 0 }));
            }}
            onPressEnter={() => programInvoicesQuery.refetch()}
            style={{ width: 300 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => setSearchQuery({ page: 0, size: 20, keyword: '' })}>검색 초기화</Button>
            <Button
              type="primary"
              onClick={() => {
                setModalMode('create');
                setSelectedProgramInvoice(undefined);
                setIsEditModalOpen(true);
              }}>
              수강증 발송
            </Button>
          </div>
        </div>
        <Table
          columns={TABLE_COLUMNS}
          bordered
          scroll={{ x: 'max-content' }}
          style={{ whiteSpace: 'pre-wrap' }}
          dataSource={programInvoicesQuery.data?.contents || []}
          pagination={{
            current: programInvoicesQuery.data?.page ? programInvoicesQuery.data.page + 1 : 1,
            pageSize: programInvoicesQuery.data?.size,
            total: programInvoicesQuery.data?.total,
          }}
          loading={programInvoicesQuery.isLoading}
          onChange={(e) => {
            setSearchQuery((query) => ({
              ...query,
              page: (e.current || 1) - 1,
              size: e.pageSize || 20,
            }));
          }}
        />
      </div>
      {isEditModalOpen && (
        <ProgramInvoiceModal
          mode={modalMode}
          programInvoice={selectedProgramInvoice}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            setIsEditModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['admin-program-invoices'] });
          }}
        />
      )}
      {isViewModalOpen && selectedProgramInvoice && (
        <Modal
          title="수강증 상세 보기"
          open={true}
          onCancel={() => setIsViewModalOpen(false)}
          footer={null}
          width={800}>
          <ProgramInvoiceDetailView invoice={selectedProgramInvoice} />
        </Modal>
      )}
    </div>
  );
};

export default ProgramInvoicesPage;
